import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { DatePicker } from "@mui/x-date-pickers"
import { OrderStatusAutocomplete } from "components/OrderStatusAutocomplete/OrderStatusAutocomplete"
import { ZahlartForm } from "components/ZahlartForm/ZahlartForm"
import dayjs, { Dayjs } from "dayjs"
import {
  Address,
  CustomerOrder,
  OrderDetailsMore,
  OrderItemInput,
  OrderItemsCancelInput,
  Printer,
  Timeline,
} from "generated/graphql"
import { enqueueSnackbar } from "notistack"
import { CustomerEditPageAddress } from "pages/CustomerEditPage/CustomerEditPageAddress"
import { fetchOrderCancel } from "queries/fetchOrderCancel"
import { fetchOrderChange } from "queries/fetchOrderChange"
import { fetchPrinters } from "queries/fetchPrinters"
import { orderByID } from "queries/orderById"
import { orderItemsCancel } from "queries/orderItemsCancel"
import { orderTimeline } from "queries/orderTimeline"
import { printDeliveryNote } from "queries/printDeliveryNote"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CancelOrderDialog } from "./CancelOrderDialog"
import { OrderArticles } from "./OrderArticles"
import { OrderPackageStatus } from "./OrderPackageStatus"
import { OrderTimeline } from "./OrderTimeline"
import { CollapsibleTable } from "components/CollapsibleTable/CollapsibleTable"
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro"
import { getEuro } from "pages/CustomerEditPage/CustomerEditPageOrders"
import { dataGridDEde } from "constants/dataGridLocale"
import { customerRelatedOrders } from "queries/customerOrders"
import { AddOrderDialog } from "pages/CustomerEditPage/AddOrderDialog"
import { exportBill } from "queries/exportBill"

const selectedPrinterEditOrderPageKey = "selectedPrinterEditOrderPage"

const relatedOrderColumns: GridColDef<CustomerOrder>[] = [
  {
    field: "orderID",
    headerName: "Order ID",
    width: 100,
  },
  {
    field: "urorder",
    headerName: "Urorder",
    width: 100,
  },
  {
    field: "datum",
    headerName: "Datum",
    width: 100,
    type: "date",
    valueGetter: (_,row) => {
      if (row.datum === null || row.datum === undefined) {
        // return 1970 for all invalid dates
        return dayjs("1970-01-01")
      }
      return dayjs(row.datum)
    },
    valueFormatter: (_,row) => {
      return dayjs(row.datum).format("DD.MM.YYYY")
    },
    sortComparator: (v1: Dayjs, v2: Dayjs) => {
      return v1.unix() - v2.unix()
    },
  },
  {
    field: "zieldat",
    headerName: "Zieldatum",
    width: 180,
  },
  {
    field: "status",
    headerName: "Status",
    width: 80,
  },
  {
    field: "zahlart",
    headerName: "Zahlart",
    width: 120,
  },
  {
    field: "rechsum",
    headerName: "Summe",
    width: 120,
    valueGetter: (_, row) =>
      getEuro(row.rechsum ? row.rechsum : 0),
  },
  {
    field: "bezahlt",
    headerName: "Bezahlt",
    width: 100,
    valueGetter: (_, row) => getEuro(row.bezahlt ? row.bezahlt : 0),
  },
  {
    field: "frachtpausch",
    headerName: "Fracht",
    width: 100,
    valueGetter: (_, row) => getEuro(row.frachtpausch ? row.frachtpausch : 0),
  },

  {
    field: "notiz",
    headerName: "Notiz",
    width: 200,
  },
  {
    field: "custnote",
    headerName: "Kundennotiz",
    width: 400,
  },
]

export const OrderDetailsPage: React.FC = () => {
  const { orderID } = useParams()
  const [order, setOrder] = useState<OrderDetailsMore>()
  const [orderBackup, setOrderBackup] = useState<OrderDetailsMore>()
  const [openAddress, setOpenAdress] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [orderChanged, setOrderChanged] = useState(false)
  const [orderItemsChanged, setOrderItemsChanged] = useState(false)
  const [timeline, setTimeline] = useState<Timeline[]>([])
  const [lieferadresse, setLieferadresse] = useState<Address | null>(null)
  const [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null)
  const [printers, setPrinters] = useState<Printer[]>([])
  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [relatedOrders, setRelatedOrders] = useState<CustomerOrder[]>([])
  const [packstopIsOpen, setPackstopIsOpen] = useState(false)
  const [addOrderDialogIsOpen, setAddOrderDialogIsOpen] = useState(false)
  const navigate = useNavigate()

  const handleOrderItemsChanged = () => {
    setOrderChanged(true)
    setOrderItemsChanged(true)
  }

  useEffect(() => {
    if (orderID !== undefined && orderID !== null && orderID !== "") {
      setIsLoadingData(true)
      // reset order
      setOrder(undefined)
      setOrderBackup(undefined)
      setOrderChanged(false)
      setOrderItemsChanged(false)
      setTimeline([])
      setLieferadresse(null)
      orderByID(parseInt(orderID))
        .then((result) => {
          if (result && result.orderByID) {
            setOrder(result.orderByID)
            setOrderBackup(result.orderByID)
          }
        })
        .finally(() => {
          setIsLoadingData(false)
        })

      orderTimeline(parseInt(orderID)).then((result) => {
        if (result) {
          setTimeline(result.orderTimeline)
        }
      })
      customerRelatedOrders(parseInt(orderID)).then((result) => {
        if (result && result.customerRelatedOrders) {
          setRelatedOrders(result.customerRelatedOrders)
        }
      })
    }
  }, [orderID])

  useEffect(() => {
    // load printer from server
    fetchPrinters().then((data) => {
      if (data?.printers) {
        setPrinters(data.printers)
        const sp = localStorage.getItem(selectedPrinterEditOrderPageKey)
        if (sp) {
          const printer = data.printers.find(
            (printer) => printer.printerID === Number(sp)
          )
          if (printer) {
            setSelectedPrinter(printer)
          }
        }
      }
    })
  }, [])

  const reloadData = () => {
    if (orderID !== undefined && orderID !== null && orderID !== "") {
      setIsLoadingData(true)
      // reset order
      setOrder(undefined)
      setOrderBackup(undefined)
      setOrderChanged(false)
      setOrderItemsChanged(false)
      setTimeline([])
      setLieferadresse(null)
      orderByID(parseInt(orderID))
        .then((result) => {
          if (result && result.orderByID) {
            setOrder(result.orderByID)
            setOrderBackup(result.orderByID)
          }
        })
        .finally(() => {
          setIsLoadingData(false)
        })

      orderTimeline(parseInt(orderID)).then((result) => {
        if (result) {
          setTimeline(result.orderTimeline)
        }
      })
    }
  }

  const changeAddress = () => {
    if (lieferadresse?.id === order?.deliveryAdr.id) {
      closeAddress()
      return
    }
    if (order && lieferadresse)
      setOrder({ ...order, deliveryAdr: lieferadresse })
    setOrderChanged(true)
    closeAddress()
  }

  const closeAddress = () => {
    setOpenAdress(false)
    setLieferadresse(null)
  }

  const handleOrderStatus = (newStatus: number) => {
    if (order) {
      setOrder({ ...order, status: newStatus })
      const articles: OrderItemInput[] = []
      if (orderItemsChanged) {
        for (const article of order.orderItems) {
          if (article.splitstatus) {
            // filter out split articles WILL NOT BE changeable
            continue
          }
          articles.push({
            ewId: article.ewID,
            bdb: article.artnum,
            stueckpreis: article.stueckpreis,
            anzahl: article.anzahl,
          })
        }
      }
      fetchOrderChange(
        order.frachtpausch,
        order.custnote,
        order.deliveryAdr.id,
        order.notiz,
        order.orderID.toString(),
        order.rabatt,
        order.secretnote,
        newStatus,
        order.zieldat,
        articles
      )
        .then((data) => {
          if (data?.orderChange) {
            enqueueSnackbar("Änderungen gespeichert", {
              variant: "success",
            })
          }
        })
        .finally(() => {
          orderByID(order.orderID)
            .then((result) => {
              if (result && result.orderByID) {
                setOrder(result.orderByID)
                setOrderBackup(result.orderByID)
                orderTimeline(order.orderID).then((result) => {
                  if (result) {
                    setTimeline(result.orderTimeline)
                  }
                })
              }
            })
            .finally(() => {
              setOrderChanged(false)
              setIsLoadingData(false)
            })
        })
    }
  }

  const resetOrder = () => {
    setOrder(orderBackup)
    setOrderChanged(false)
  }

  const saveOrder = () => {
    if (order) {
      const articles: OrderItemInput[] = []
      if (orderItemsChanged) {
        for (const article of order.orderItems) {
          if (article.splitstatus) {
            // filter out split articles WILL NOT BE changeable
            continue
          }
          articles.push({
            ewId: article.ewID,
            bdb: article.artnum,
            stueckpreis: article.stueckpreis,
            anzahl: article.anzahl,
          })
        }
      }
      fetchOrderChange(
        order.frachtpausch,
        order.custnote,
        order.deliveryAdr.id,
        order.notiz,
        order.orderID.toString(),
        order.rabatt,
        order.secretnote,
        order.status,
        order.zieldat,
        articles
      )
        .then((data) => {
          if (data?.orderChange) {
            enqueueSnackbar("Änderungen gespeichert", {
              variant: "success",
            })
          }
        })
        .finally(() => {
          orderByID(order.orderID)
            .then((result) => {
              if (result && result.orderByID) {
                setOrder(result.orderByID)
                setOrderBackup(result.orderByID)
                orderTimeline(order.orderID).then((result) => {
                  if (result) {
                    setTimeline(result.orderTimeline)
                  }
                })
              }
            })
            .finally(() => {
              setOrderChanged(false)
              setIsLoadingData(false)
            })
        })
    }
  }

  const handlePrintDelieveryNote = () => {
    if (selectedPrinter && orderID) {
      const printerIpPort =
        selectedPrinter.printerIP + ":" + selectedPrinter.printerPort
      printDeliveryNote(parseInt(orderID), printerIpPort).then((data) => {
        if (data?.printDeliveryNote) {
          enqueueSnackbar("Lieferschein wird gedruckt", {
            variant: "info",
          })
        }
      })
    }
  }

  const handleSendBillToCust = async (_: any) => {
    if (order) {
      exportBill(order.orderID).then((data) => {
        if (data) {
          enqueueSnackbar("Rechnung verschickt!", {
            variant: "success",
          })
          orderTimeline(order.orderID).then((result) => {
            if (result) {
              setTimeline(result.orderTimeline)
            }
          })
        }
      })
    }
  }

  const handleCancle = (value: OrderItemsCancelInput) => {
    if (
      order?.orderID !== undefined &&
      order.orderID !== null &&
      order.orderID !== 0
    ) {
      if (value.orderItems.length > 0) {
        // partial cancel
        const input: OrderItemsCancelInput = {
          ...value,
          orderID: order.orderID.toString(),
        }
        orderItemsCancel({ in: input })
          .then((data) => {
            if (data?.orderItemsCancel === true) {
              enqueueSnackbar("Artikelpositionen storniert", {
                variant: "success",
              })
            }
          })
          .finally(() => {
            setOpenCancelDialog(false)
            reloadData()
          })
      } else {
        if (order.bezahlt === 0) {
          handleOrderStatus(value.targetStatus)
          return
        }
        fetchOrderCancel(
          order.orderID.toString(),
          value.action,
          value.targetStatus
        )
          .then((data) => {
            if (data?.orderCancel) {
              enqueueSnackbar("Auftrag storniert", {
                variant: "success",
              })
            }
          })
          .finally(() => {
            setOpenCancelDialog(false)
            reloadData()
          })
      }
    }
  }

  const adressEditble = (): boolean => {
    if (order && order.packages.length > 0) return false
    return true
  }

  return (
    <Container maxWidth={false}>
      {isLoadingData && (
        <Box sx={{ height: "185px" }}>
          <Typography>Auftrag wird geladen...</Typography>
        </Box>
      )}
      {order && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid size={10}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3,
                      }}
                    >
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={12}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Details
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <Button
                            onClick={() =>
                              navigate("/editCustomer/" + order.kundnum)
                            }
                            variant="contained"
                            fullWidth
                          >
                            Kundennummer: {order.kundnum}
                          </Button>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Bestellnr."
                            value={order.orderID}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Urorder"
                            value={order.urorder}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <OrderStatusAutocomplete
                            status={order.status}
                            handleOrderStatusChange={handleOrderStatus}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Runde"
                            value={order.roundName}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="SH"
                            value={order.sh}
                            disabled
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3,
                      }}
                    >
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={12}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Zahlungen
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Rechnungssumme"
                            value={order.rechsum.toFixed(2)}
                            disabled
                            fullWidth
                            type="number"
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    €
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Frachtpauschale"
                            value={order.frachtpausch}
                            fullWidth
                            type="number"
                            onChange={(e) => {
                              if (parseFloat(e.target.value) >= 0) {
                                setOrder({
                                  ...order,
                                  frachtpausch: parseFloat(e.target.value),
                                })
                                setOrderChanged(true)
                              }
                            }}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    €
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Rabatt"
                            value={order.rabatt}
                            fullWidth
                            type="number"
                            onChange={(e) => {
                              if (parseFloat(e.target.value) >= 0) {
                                setOrder({
                                  ...order,
                                  rabatt: parseFloat(e.target.value),
                                })
                                setOrderChanged(true)
                              }
                            }}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    %
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Bezahlt"
                            value={order.bezahlt}
                            disabled
                            fullWidth
                            type="number"
                            onChange={(e) => {
                              if (parseFloat(e.target.value) >= 0) {
                                setOrder({
                                  ...order,
                                  bezahlt: parseFloat(e.target.value),
                                })
                                setOrderChanged(true)
                              }
                            }}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    €
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <ZahlartForm
                            disabled={true}
                            value={order.zahlart}
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                zahlart: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>

                        <Grid size={12}>
                          <TextField
                            label="Zieldatum"
                            value={order.zieldat}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <DatePicker
                            disablePast
                            label="Zieldatum setzen"
                            sx={{ width: "100%", height: "100%" }}
                            onChange={(date: Dayjs | null) => {
                              if (date) {
                                setOrder({
                                  ...order,
                                  zieldat:
                                    "festes Datum (" +
                                    date.format("DD.MM.YYYY") +
                                    ")",
                                })
                                setOrderChanged(true)
                              }
                            }}
                          />
                        </Grid>

                        <Grid size={6}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setOrder({ ...order, zieldat: "Standard ()" })
                              setOrderChanged(true)
                            }}
                          >
                            Zieldatum zurücksetzen
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3,
                      }}
                    >
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={12}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Coupons
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Coupon Key"
                            disabled
                            value={order.couponKey}
                            fullWidth
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                couponKey: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Coupon Wert"
                            disabled
                            value={order.couponValue}
                            fullWidth
                            type="number"
                            onChange={(e) => {
                              if (parseFloat(e.target.value) >= 0) {
                                setOrder({
                                  ...order,
                                  couponValue: parseFloat(e.target.value),
                                })
                                setOrderChanged(true)
                              }
                            }}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    €
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={12}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Rücküberweisung
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Betrag"
                            value={order.rue}
                            disabled
                            fullWidth
                            type="number"
                            onChange={(e) => {
                              if (parseFloat(e.target.value) >= 0) {
                                setOrder({
                                  ...order,
                                  rue: parseFloat(e.target.value),
                                })
                                setOrderChanged(true)
                              }
                            }}
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    €
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Text"
                            value={order.rueText}
                            disabled
                            fullWidth
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                rueText: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3,
                      }}
                    >
                      {order.deliveryAdr && (
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid
                            size={{
                              xs: 12,
                              md: 6,
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="div"
                              gutterBottom
                            >
                              Lieferadresse
                            </Typography>
                          </Grid>
                          {adressEditble() && (
                            <Grid
                              size={{
                                xs: 12,
                                md: 6,
                              }}
                            >
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => setOpenAdress(true)}
                              >
                                Ändern
                              </Button>
                            </Grid>
                          )}
                          {!adressEditble() && (
                            <Grid
                              size={{
                                xs: 12,
                                md: 6,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  flex: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color="error"
                                  gutterBottom
                                >
                                  Adresse nicht änderbar
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          <Grid size={12}>
                            <Typography variant="body2">
                              Firma: {order.deliveryAdr.company}
                            </Typography>
                            <Typography variant="body2">
                              {order.deliveryAdr.anrede}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ wordWrap: "break-word" }}
                            >
                              {order.deliveryAdr.fullName}
                            </Typography>
                            <Typography variant="body2">
                              {order.deliveryAdr.street}
                            </Typography>
                            <Typography variant="body2">
                              {order.deliveryAdr.zipCode}{" "}
                              {order.deliveryAdr.city}
                            </Typography>
                            <Typography variant="subtitle2">
                              {order.deliveryAdr.country}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}

                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid size={12}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Notizen
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Notiz"
                            value={order.notiz}
                            fullWidth
                            multiline
                            minRows={4}
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                notiz: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="custnote"
                            value={order.custnote}
                            fullWidth
                            multiline
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                custnote: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="secretnote"
                            value={order.secretnote}
                            fullWidth
                            multiline
                            onChange={(e) => {
                              setOrder({
                                ...order,
                                secretnote: e.target.value,
                              })
                              setOrderChanged(true)
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={resetOrder}
                    disabled={orderChanged === false}
                    color="error"
                  >
                    Änderungen zurücksetzen
                  </Button>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!orderChanged}
                    onClick={saveOrder}
                  >
                    Änderungen speichern
                  </Button>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="printer-select-label">Drucker</InputLabel>
                    <Select
                      labelId="printer-select-label"
                      id="printer-select"
                      value={selectedPrinter?.printerID || ""}
                      label="Drucker"
                      onChange={(event) => {
                        const printerID = event.target.value as number
                        const printer = printers.find(
                          (printer) => printer.printerID === printerID
                        )
                        setSelectedPrinter(printer || null)
                        localStorage.setItem(
                          selectedPrinterEditOrderPageKey,
                          printerID.toString()
                        )
                      }}
                    >
                      {printers.map((printer) => (
                        <MenuItem
                          key={printer.printerID}
                          value={printer.printerID}
                        >
                          {printer.printerID} - {printer.printerIP}:
                          {printer.printerPort}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{ height: "100%" }}
                    onClick={handlePrintDelieveryNote}
                    fullWidth
                    disabled={selectedPrinter === null}
                  >
                    Lieferschein drucken
                  </Button>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ height: "100%" }}
                    onClick={handleSendBillToCust}
                  >
                    Rechnung senden
                  </Button>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ height: "100%" }}
                    onClick={() => {
                      if (order.status === 15 || order.status === 17) {
                        enqueueSnackbar("Auftrag bereits storniert", {
                          variant: "info",
                        })
                        return
                      }
                      setOpenCancelDialog(true)
                    }}
                  >
                    Auftrag stornieren
                  </Button>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ height: "100%" }}
                    onClick={() => {
                      if (
                        order.status === 15 ||
                        order.status === 17 ||
                        order.status === 801
                      ) {
                        enqueueSnackbar(
                          "Auftrag bereits storniert oder Packstopp!",
                          {
                            variant: "info",
                          }
                        )
                        return
                      }
                      setPackstopIsOpen(true)
                    }}
                  >
                    Packstopp!
                  </Button>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ height: "100%" }}
                    onClick={() => {
                      if (order.status === 15 || order.status === 17) {
                        enqueueSnackbar("Auftrag bereits storniert", {
                          variant: "info",
                        })
                        return
                      }
                      setAddOrderDialogIsOpen(true)
                    }}
                  >
                    Nachlieferung anlegen
                  </Button>
                </Grid>

                <Grid size={12}>
                  <OrderArticles
                    order={order}
                    setOrder={setOrder}
                    reloadData={reloadData}
                    setOrderItemsChanged={handleOrderItemsChanged}
                  />
                </Grid>
                <Grid size={12}>
                  <OrderPackageStatus packages={order.packages} />
                </Grid>
                <Grid size={12}>
                  <CollapsibleTable
                    defaultOpen={true}
                    title={`Verwandte Aufträge (${relatedOrders.length})`}
                  >
                    <Box sx={{ height: 600 }}>
                      <DataGridPro
                        rows={relatedOrders}
                        getRowId={(row) => row.orderID}
                        columns={relatedOrderColumns}
                        localeText={dataGridDEde}
                        rowHeight={60}
                        slotProps={{
                          toolbar: {
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 100 },
                          },
                        }}
                        onRowClick={(params) => {
                          let id = params.id
                          if (typeof id === "string") {
                            // on auto-generated rows, the id is a string and contains wrong route params auto-generated-row-null/
                            id = id.replace("auto-generated-row-null/", "")
                          }
                          navigate(`/orderDetails/${id}`)
                        }}
                        disableColumnFilter
                        disableColumnSelector
                        disableDensitySelector
                      />
                    </Box>
                  </CollapsibleTable>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={2}>
              <OrderTimeline
                timeline={timeline}
                setTimeline={setTimeline}
                orderId={order.orderID}
              />
            </Grid>
          </Grid>
          <Dialog
            open={openAddress}
            onClose={closeAddress}
            fullWidth
            maxWidth={false}
          >
            <DialogTitle>Lieferadresse auswählen</DialogTitle>
            <DialogContent>
              <CustomerEditPageAddress
                customerID={order.kundnum.toString()}
                setSelectedAddress={setLieferadresse}
                open={true}
              />
            </DialogContent>
            <DialogActions>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={closeAddress}
              >
                Abbrechen
              </Button>
              <Button fullWidth variant="contained" onClick={changeAddress}>
                Speichern
              </Button>
            </DialogActions>
          </Dialog>

          <CancelOrderDialog
            open={openCancelDialog}
            close={() => setOpenCancelDialog(false)}
            handleCancel={handleCancle}
            orderItems={order.orderItems}
            order={order}
          />
          <Dialog
            open={packstopIsOpen}
            onClose={() => setPackstopIsOpen(false)}
          >
            <DialogTitle>Packstopp</DialogTitle>
            <DialogContent>
              <Typography>
                Wollen Sie wirklich den Packstopp für den Auftrag{" "}
                {order.orderID} setzen ?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={() => setPackstopIsOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleOrderStatus(801)
                  setPackstopIsOpen(false)
                }}
              >
                Packstopp setzen
              </Button>
            </DialogActions>
          </Dialog>
          <AddOrderDialog
            customerID={order.kundnum.toString()}
            open={addOrderDialogIsOpen}
            close={() => setAddOrderDialogIsOpen(false)}
            closeNewOrder={() => {
              setAddOrderDialogIsOpen(false)
              reloadData()
            }}
            previousOrderId={order.orderID}
          />
        </Box>
      )}
    </Container>
  )
}
